import { defineStore } from 'pinia'
import { Cookie } from '@/utils'

export interface Area {
  area_id: number
  name: string
  sort: number
  language_id: number
  laravel_through_key: number
}

export interface CountryInfo {
  id: number
  iso_code: string
  currency: string
  country_name: string
  countries_name?: string
  countries_chinese_name?: string
  en_countries_name?: string
  website: string
  language: string
  languageOriginalForRequest?: string
  area?: Area[]
  countries_id: number
  language_id?: number
  warehouse?: string
  isEuUnion?: boolean
  locale?: string
  tel_prefix?: string
  timezone?: string
  symbol: string
  seo_lang_code?: string
  citys?: unknown[]
  isCookie?: unknown
  webPhone?: unknown
  extend?: unknown
}

export type WebsiteInfoSource = Omit<Partial<CountryInfo>, 'id' | 'countries_id' | 'language_id' | 'isEuUnion'> & {
  id?: number | string | null
  countries_id?: number | string | null
  language_id?: number | string | null
  isEuUnion?: boolean | number | string | null
}

const WEBSITE_INFO_COOKIE = 'fs_websiteinfo'
const WEBSITE_INFO_MAX_AGE = 60 * 60 * 24 * 30

const DEFAULT_WEBSITE: CountryInfo = {
  id: 302,
  iso_code: 'US',
  currency: 'USD',
  country_name: 'United States',
  countries_name: 'United States',
  en_countries_name: 'United States',
  website: 'en',
  language: 'English',
  languageOriginalForRequest: 'English',
  area: [{ area_id: 1, name: 'North America', sort: 0, language_id: 1, laravel_through_key: 302 }],
  countries_id: 223,
  language_id: 1,
  warehouse: 'US',
  isEuUnion: false,
  locale: 'en',
  tel_prefix: '+1',
  timezone: 'America/New_York',
  symbol: '$',
  seo_lang_code: 'en',
  citys: [],
}

const pickString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return ''
}

const toNumber = (value: unknown, fallback: number) => {
  if (value === undefined || value === null || value === '') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const toOptionalNumber = (value: unknown, fallback: number | undefined, useFallback: boolean) => {
  if (value === undefined || value === null || value === '') {
    return useFallback ? fallback : undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : useFallback ? fallback : undefined
}

const toOptionalBoolean = (value: unknown, fallback: boolean | undefined, useFallback: boolean) => {
  if (typeof value === 'boolean') return value
  if (value === 1 || value === '1' || value === 'true') return true
  if (value === 0 || value === '0' || value === 'false') return false
  return useFallback ? fallback : undefined
}

const pickOptionalValue = <T>(value: T | undefined | null, fallback: T | undefined, useFallback: boolean) => {
  if (value !== undefined && value !== null) return value
  return useFallback ? fallback : undefined
}

const cloneWebsiteInfo = (websiteInfo: CountryInfo): CountryInfo => ({
  ...websiteInfo,
  area: websiteInfo.area?.map(item => ({ ...item })),
  citys: Array.isArray(websiteInfo.citys) ? [...websiteInfo.citys] : websiteInfo.citys,
})

const normalizeWebsiteInfo = (
  source: WebsiteInfoSource = {},
  fallback: CountryInfo = DEFAULT_WEBSITE,
  useOptionalFallback = false
): CountryInfo => {
  const countryName = pickString(
    source.country_name,
    source.countries_name,
    source.en_countries_name,
    fallback.country_name
  )
  const countriesName = pickString(source.countries_name, source.country_name, fallback.countries_name, countryName)
  const website = pickString(source.website, source.locale, fallback.website)
  const locale = pickString(source.locale, source.website, useOptionalFallback ? fallback.locale : undefined)

  return {
    id: toNumber(source.id, fallback.id),
    iso_code: pickString(source.iso_code, fallback.iso_code),
    currency: pickString(source.currency, fallback.currency),
    country_name: countryName,
    countries_name: countriesName,
    countries_chinese_name: pickOptionalValue(
      source.countries_chinese_name,
      fallback.countries_chinese_name,
      useOptionalFallback
    ),
    en_countries_name: pickOptionalValue(
      source.en_countries_name || countryName,
      fallback.en_countries_name,
      useOptionalFallback
    ),
    website,
    language: pickString(source.language, fallback.language),
    languageOriginalForRequest: pickOptionalValue(
      source.languageOriginalForRequest || source.language,
      fallback.languageOriginalForRequest,
      useOptionalFallback
    ),
    area: pickOptionalValue(source.area, fallback.area, useOptionalFallback),
    countries_id: toNumber(source.countries_id, fallback.countries_id),
    language_id: toOptionalNumber(source.language_id, fallback.language_id, useOptionalFallback),
    warehouse: pickOptionalValue(source.warehouse, fallback.warehouse, useOptionalFallback),
    isEuUnion: toOptionalBoolean(source.isEuUnion, fallback.isEuUnion, useOptionalFallback),
    locale,
    tel_prefix: pickOptionalValue(source.tel_prefix, fallback.tel_prefix, useOptionalFallback),
    timezone: pickOptionalValue(source.timezone, fallback.timezone, useOptionalFallback),
    symbol: pickString(source.symbol, fallback.symbol),
    seo_lang_code: pickOptionalValue(source.seo_lang_code, fallback.seo_lang_code, useOptionalFallback),
    citys: pickOptionalValue(source.citys, fallback.citys, useOptionalFallback),
    isCookie: source.isCookie !== undefined ? source.isCookie : useOptionalFallback ? fallback.isCookie : undefined,
    webPhone: source.webPhone !== undefined ? source.webPhone : useOptionalFallback ? fallback.webPhone : undefined,
    extend: source.extend !== undefined ? source.extend : useOptionalFallback ? fallback.extend : undefined,
  }
}

const getStoredSite = () => {
  try {
    return localStorage.getItem('design_center_site')
  } catch (error) {
    return null
  }
}

const getWebsiteInfoCookieValues = () => {
  if (typeof document === 'undefined') return []
  const cookieName = `${encodeURIComponent(WEBSITE_INFO_COOKIE)}=`

  return document.cookie
    .split(/;\s*/)
    .filter(cookie => cookie.startsWith(cookieName))
    .map(cookie => decodeURIComponent(cookie.slice(cookieName.length)))
}

const parseWebsiteInfo = (websiteInfoCookie: string) => {
  try {
    const parsed = JSON.parse(websiteInfoCookie) as WebsiteInfoSource
    return parsed
  } catch (error) {
    console.error('fs_websiteinfo JSON parse error:', error)
    return null
  }
}

const getWebsiteInfoFromCookie = () => {
  const websiteInfoCookies = getWebsiteInfoCookieValues()
  const fallbackCookie = Cookie.get(WEBSITE_INFO_COOKIE)
  const cookieValues = websiteInfoCookies.length ? websiteInfoCookies : fallbackCookie ? [fallbackCookie] : []
  if (!cookieValues.length) return cloneWebsiteInfo(DEFAULT_WEBSITE)

  const websiteInfos = cookieValues.map(parseWebsiteInfo).filter((item): item is WebsiteInfoSource => Boolean(item))
  if (!websiteInfos.length) return cloneWebsiteInfo(DEFAULT_WEBSITE)

  const storedSite = getStoredSite()
  const matchedWebsiteInfo = storedSite
    ? websiteInfos.find(item => item.website === storedSite || item.locale === storedSite)
    : null

  return normalizeWebsiteInfo(matchedWebsiteInfo || websiteInfos[websiteInfos.length - 1])
}

const setPersistentCookie = (name: string, value: string) => {
  Cookie.set(name, '', { maxAge: -1, domain: '' })
  Cookie.set(name, '', { maxAge: -1 })
  Cookie.set(name, value, { maxAge: WEBSITE_INFO_MAX_AGE, domain: '' })
  Cookie.set(name, value, { maxAge: WEBSITE_INFO_MAX_AGE })
}

const persistWebsiteInfo = (websiteInfo: CountryInfo) => {
  setPersistentCookie(WEBSITE_INFO_COOKIE, JSON.stringify(websiteInfo))
  setPersistentCookie('website', websiteInfo.website)
  setPersistentCookie('language', websiteInfo.language)
  setPersistentCookie('currency', websiteInfo.currency)
  setPersistentCookie('iso_code', websiteInfo.iso_code)
}

export const useWebsiteInfoStore = defineStore('websiteInfo', {
  state: (): CountryInfo => getWebsiteInfoFromCookie(),
  actions: {
    updateWebsiteInfo(websiteInfo: WebsiteInfoSource) {
      const nextWebsiteInfo = normalizeWebsiteInfo(websiteInfo, this.$state)
      this.$patch(nextWebsiteInfo)
      persistWebsiteInfo(nextWebsiteInfo)
    },
  },
})
